/**
 * The arithmetic AST, and nothing that depends on the field catalogue.
 *
 * It lives here rather than beside the companion editor because
 * `condition-utils` has to recognise an expression operand (its
 * dangling-reference guard must judge the fields an expression READS, not the
 * sentinel key it wears) while the editor's own module imports
 * `condition-utils` for `getFieldKey` / `findFieldByKey`. A leaf both can
 * import keeps that from becoming an import cycle, and keeps the wire shape in
 * one place.
 */
export type ExprNode =
  | { t: "n"; v: number }
  | { t: "f"; k: string }
  | { t: "op"; o: ExprOperator; l: ExprNode; r: ExprNode };

export type ExprOperator = "+" | "-" | "*" | "/";

export const EXPR_OPERATORS: ExprOperator[] = ["+", "-", "*", "/"];

/**
 * Universally-read math symbols, deliberately untranslated — the same call
 * `ConditionBuilder/operators.ts` already makes for `= ≠ > < ≥ ≤`, and for the
 * same reason: a symbol is faster to scan than a word for a non-technical
 * reader, and it saves four keys × 57 locales that would all say the same
 * thing.
 */
export const EXPR_OPERATOR_GLYPHS: Record<ExprOperator, string> = {
  "+": "+",
  "-": "−",
  "*": "×",
  "/": "÷",
};

/** Mirrors `ExpressionAstValidator.MAX_EXPR_LITERAL` on the JVM and
 *  `MAX_EXPR_LITERAL` in the Worker. */
export const MAX_LIMIT_EXPR_LITERAL = 1_000_000;

/**
 * Nesting bound, mirroring `ExpressionAstValidator.MAX_EXPR_DEPTH` on the JVM
 * and `MAX_EXPR_DEPTH` in the Worker's `expr/ast.ts`.
 *
 * The editor needs the number, not just the readers: the strip folds its terms
 * LEFT-ASSOCIATIVELY, so N operators nest N+1 deep and the editor's own cap has
 * to come from here. It was once reasoned about against the NODE bound instead
 * ("12 terms is 23 nodes"), which let the strip build trees the backend refused
 * — with a message about companion rules, in dialogs that are not companion
 * rules.
 *
 * `readExprNode` below stays deliberately LOOSER (12): the cockpit only ever
 * DISPLAYS what it reads, and refusing to draw a stored rule is worse than
 * drawing a deep one.
 */
export const MAX_EXPR_DEPTH = 8;

/**
 * The sentinel key a filter carries when its left operand is an expression.
 *
 * It is not a guest field and not `additionalFields.`-prefixed, so
 * main-backend's key allowlist already refuses it on every other surface —
 * workflows, quotas, counting conditions, check-in devices, guest queries.
 * Containment is a property of that allowlist rather than of a check somebody
 * has to remember to add.
 *
 * The alternative — hanging the expression beside a REAL field key — degrades,
 * for any reader that ignores it, into a plausible but different predicate
 * (`(tickets − used) > 0` becomes `tickets > 0`). The sentinel degrades to
 * nothing at all, which is the point.
 */
export const EXPR_FILTER_KEY = "#expr";

/** Wire version. The JVM refuses anything else, so a future dialect cannot be
 *  stored and then misread by today's Worker. */
export const LIMIT_EXPR_VERSION = 1;

/** Tolerant structural read of a stored tree. Mirrors the Worker's
 *  `readExprNode`, minus the budget — the cockpit only ever DISPLAYS what it
 *  reads, and refusing to display a rule is worse than showing a deep one. */
export function readExprNode(raw: unknown, depth = 1): ExprNode | null {
  if (depth > 12) return null;
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const node = raw as Record<string, unknown>;
  if (node.t === "n") {
    return typeof node.v === "number" && Number.isFinite(node.v) ? { t: "n", v: node.v } : null;
  }
  if (node.t === "f") {
    return typeof node.k === "string" && node.k.trim() !== "" ? { t: "f", k: node.k.trim() } : null;
  }
  if (node.t === "op") {
    const o = node.o;
    if (typeof o !== "string" || !EXPR_OPERATORS.includes(o as ExprOperator)) return null;
    const l = readExprNode(node.l, depth + 1);
    const r = readExprNode(node.r, depth + 1);
    if (l === null || r === null) return null;
    return { t: "op", o: o as ExprOperator, l, r };
  }
  return null;
}

/** Every guest field the tree reads. */
export function expressionFieldKeys(node: ExprNode): string[] {
  if (node.t === "f") return [node.k];
  if (node.t === "op") return [...expressionFieldKeys(node.l), ...expressionFieldKeys(node.r)];
  return [];
}

/**
 * A readable form of the tree, given a resolver for field labels.
 *
 * The resolver is a parameter rather than a `Field[]` so this stays a leaf:
 * the condition row's chip renderer and the companion editor both print the
 * same tree, and neither has to import the other.
 */
export function printExprNode(node: ExprNode, label: (key: string) => string): string {
  const walk = (n: ExprNode, parenthesise: boolean): string => {
    if (n.t === "n") return String(n.v);
    if (n.t === "f") return label(n.k);
    const inner = `${walk(n.l, true)} ${EXPR_OPERATOR_GLYPHS[n.o]} ${walk(n.r, true)}`;
    return parenthesise ? `(${inner})` : inner;
  };
  return walk(node, false);
}
